import { reviewsRedirectionVarValue } from '../constants/lib/constants';
import {
  PropertyCardEntry,
  PropertyCardInputEdge,
  PropertyCardInputItem,
  PropertyCardProperty,
  PropertyCardSelection,
  PropertyCardSourceData,
  PropertyCardViewModel,
} from '../types/propertyCard.types';
import { Message } from '../store';
import { computeRedirectionUrl } from './computeRedirectionUrl';

/** Align with {@link MPropertyReviewRating}: prefer document locale for link generation. */
function resolveLocaleForReviewLinks(): string {
  if (typeof document !== 'undefined' && document.documentElement?.lang) {
    return document.documentElement.lang;
  }
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }
  return 'en-US';
}

// Resolve the best review URL for the card (parity with HQV `HotelGalleryTitleComp` + `useRedirectionRules`).
// Priority order:
// 1) top-level property.reviewUrl
// 2) property.basicInformation.reviewUrl
// 3) `computeRedirectionUrl` with seoNickname or Marsha + slugified name (same as HQV when GraphQL omits review URLs)
// 4) legacy absolute fallback when only `seoNickname` is present without Marsha/name
// Returning undefined here allows the caller to decide the final UI fallback.
export function getPropertyReviewUrl(property?: PropertyCardProperty): string | undefined {
  if (property?.reviewUrl) return property.reviewUrl;
  if (property?.basicInformation?.reviewUrl) return property.basicInformation.reviewUrl;

  const basic = property?.basicInformation;
  const propertyName = basic?.name ?? basic?.nameInDefaultLanguage;
  const marsha = property?.id;
  const brandCode = basic?.brand?.id;
  const seoNickname = property?.seoNickname ?? null;
  const locale = resolveLocaleForReviewLinks();

  if (marsha && propertyName) {
    const hqvAligned = computeRedirectionUrl(
      brandCode,
      marsha,
      propertyName,
      reviewsRedirectionVarValue,
      locale,
      seoNickname
    );
    if (hqvAligned) return hqvAligned;
  }

  if (seoNickname) {
    return `https://www.marriott.com/en-us/hotels/${seoNickname}/reviews/`;
  }
  return undefined;
}

// Convert one selected property card entry (formatter item + matched search edge)
// into a UI-ready view model.
export function toPropertyCardViewModel<TItem extends PropertyCardInputItem, TEdge extends PropertyCardInputEdge>(
  entry: PropertyCardEntry<TItem, TEdge>,
  idx: number,
  geoCodeInfo?: {
    details?: {
      location?: {
        city?: string;
        state?: string;
        country?: string;
      };
    };
  }
): PropertyCardViewModel<PropertyCardProperty, TEdge> {
  const edge = entry?.edge ?? null;
  const property = (edge?.node?.property ?? {}) as PropertyCardProperty;
  const basicInfo = property?.basicInformation;
  const aiDescription = entry?.item?.aiDescription || '';
  const ratingRaw = property?.reviews?.stars?.count;
  const reviewsRaw = property?.reviews?.numberOfReviews?.count;
  const reviewUrl = getPropertyReviewUrl(property) || '#';
  const propertyId = `${property?.id || idx}`;

  // Priority: use property-level location first, fall back to geoCodeInfo from search
  const locationSummary = {
    city: basicInfo?.city || geoCodeInfo?.details?.location?.city,
    state: basicInfo?.state || geoCodeInfo?.details?.location?.state,
    country: basicInfo?.country || geoCodeInfo?.details?.location?.country,
  };

  return {
    key: propertyId,
    propertyId,
    propertyData: property,
    propertyName: basicInfo?.nameInDefaultLanguage || 'Property',
    latitude: basicInfo?.latitude,
    longitude: basicInfo?.longitude,
    priceDisplay: basicInfo?.priceDisplay,
    locationSummary,
    ratingInfo: {
      isMax: Boolean(basicInfo?.isMax),
      reviewUrl,
      reviewTitle: `${typeof ratingRaw === 'number' ? ratingRaw.toFixed(1) : '0.0'} stars of ${
        typeof reviewsRaw === 'number' ? reviewsRaw.toLocaleString() : 0
      } reviews`,
      ratingValue: typeof ratingRaw === 'number' ? ratingRaw : 0,
      hasRating: Boolean(ratingRaw),
      reviewCountValue: typeof reviewsRaw === 'number' ? reviewsRaw : 0,
    },
    description: aiDescription,
    edge,
  };
}

// Read backend search total from either supported branch.
// Returns undefined when backend did not provide totals.
export function getRawSearchTotal<TEdge>(viewData: PropertyCardSourceData<TEdge>): number | undefined {
  const keywordTotal = viewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.total;
  if (typeof keywordTotal === 'number') return keywordTotal;
  return undefined;
}

// Read backend search edges total from either supported branch.
// Returns undefined when backend did not provide totals.
export function getRawSearchEdgesTotal<TEdge>(viewData: PropertyCardSourceData<TEdge>): number | undefined {
  const keywordEdgesTotal =
    viewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.edges?.length;
  if (typeof keywordEdgesTotal === 'number') return keywordEdgesTotal;
  return undefined;
}

// Build the list of property cards to render for chat templates.
//
// Contract:
// - We only render properties explicitly provided by formatter uiData (uiProperties).
// - When viewData is provided, use edges from that specific message's data (message-specific pricing).
// - When viewData is not provided, fall back to aggregating from all messages (legacy behavior).
//
// Selection flow:
// 1) Extract edges from viewData if provided, otherwise aggregate from all messages.
// 2) If formatter list is empty, return no propertyCards.
// 3) Index edges by property.id for O(1) matching.
// 4) Map formatter items in order and keep only successfully matched entries.
//
// This preserves formatter ordering while preventing stale/unmatched IDs
// from producing empty/broken cards in the UI.
//
// IMPORTANT: Using message-specific viewData ensures prices displayed match the search criteria
// that was active when the message was created, preventing price inconsistency when users
// modify search criteria or view older messages.
export function getPropertyCardsToRender<TItem extends PropertyCardInputItem, TEdge extends PropertyCardInputEdge>(
  uiProperties: TItem[] | undefined,
  messages: Message[],
  viewData?: {
    rawData?: { data?: { search?: { conversationalSearch?: { searchByNaturalLanguageQuery?: { edges?: TEdge[] } } } } };
  }
): PropertyCardSelection<TItem, TEdge> {
  const uiPropertyItems = Array.isArray(uiProperties) ? uiProperties : [];

  const edgesById = new Map<string, TEdge>();
  const allEdges: TEdge[] = [];

  // If viewData is provided, use edges from that specific message's data.
  // This ensures prices match the search criteria that was active when the message was created.
  if (viewData) {
    const msgEdges =
      (viewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.edges as TEdge[]) ?? [];
    msgEdges.forEach(edge => {
      const id = edge?.node?.property?.id;
      if (id) {
        edgesById.set(id, edge);
        allEdges.push(edge);
      }
    });
  } else {
    // Legacy fallback: Aggregate edges from ALL messages in the current chat session.
    // Later messages override earlier ones when same propertyId appears (most recent data wins).
    messages.forEach(message => {
      const msgEdges =
        (message?.previewData?.data?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery
          ?.edges as TEdge[]) ?? [];
      msgEdges.forEach(edge => {
        const id = edge?.node?.property?.id;
        if (id) {
          edgesById.set(id, edge);
          allEdges.push(edge);
        }
      });
    });
  }

  // If formatter did not provide any property list, render no cards by contract.
  // We still return aggregated edges because caller may use them for totals.
  if (uiPropertyItems.length === 0) {
    return { edges: allEdges, propertyCards: [] };
  }

  // Match formatter items to raw edges in the exact formatter order.
  // Each matched result keeps both:
  // - item: formatter metadata (aiDescription, etc.)
  // - edge: raw property payload from search response
  const propertyCards = uiPropertyItems
    .map(item => {
      const propertyId = item?.propertyId;
      // If formatter has propertyId, fetch raw edge from map; otherwise unmatched/null.
      const edge = propertyId ? edgesById.get(propertyId) ?? null : null;
      return { item, edge };
    })
    // Keep only successful matches so UI doesn't render incomplete/broken cards.
    .filter(entry => Boolean(entry.edge)) as PropertyCardEntry<TItem, TEdge>[];

  // Return both aggregated edges and matched property cards.
  // Caller uses propertyCards for rendering and edges for totals/secondary calculations.
  return { edges: allEdges, propertyCards };
}
